import { useEffect, useState, useRef } from "react";
import { Play, Pause, Download, FileText, Video, Lock, Music } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function Library() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();

  useEffect(() => { api.get("/resources").then((r) => setItems(r.data)); }, [user]);
  const filtered = items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24" data-testid="library-page">
      <div className="overline mb-4">{t({ en: "Client Library", fr: "Bibliothèque client" })}</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 tracking-tight" style={{ color: "var(--ink)" }}>{t({ en: "Articles, worksheets, meditations & videos.", fr: "Articles, feuillets, méditations et vidéos." })}</h1>
      <p className="text-lg max-w-2xl mb-4" style={{ color: "var(--ink-2)" }}>{t({ en: "A growing collection of tools. Some are open to anyone; others are available to clients with an account.", fr: "Une collection d'outils qui s'enrichit. Certains sont accessibles à tou·tes ; d'autres sont réservés aux client·es avec un compte." })}</p>
      {!user && (
        <div className="p-4 mb-8 rounded-lg text-sm flex items-center justify-between flex-wrap gap-3" style={{ background: "var(--sage)", color: "var(--brand)" }} data-testid="library-gate-banner">
          <span><Lock className="inline mr-2" size={16} />{t({ en: "Sign in or create an account to unlock client-only resources.", fr: "Connectez-vous ou créez un compte pour accéder aux ressources réservées aux client·es." })}</span>
          <div className="flex gap-2">
            <Link to="/login" className="btn-ghost text-sm py-2 px-4">{t({ en: "Sign in", fr: "Connexion" })}</Link>
            <Link to="/register" className="btn-primary text-sm py-2 px-4">{t({ en: "Create account", fr: "Créer un compte" })}</Link>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-10">
        {[["all", t({ en: "All", fr: "Tous" })], ["audio", "Audio"], ["pdf", "PDF"], ["video", t({ en: "Video", fr: "Vidéo" })]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all ${filter === v ? "bg-[var(--brand)] text-[#FBF9F5]" : "bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border-medium)]"}`} data-testid={`filter-${v}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((r) => <ResourceCard key={r.id} r={r} t={t} />)}
      </div>
    </div>
  );
}

function ResourceCard({ r, t }) {
  const iconMap = { audio: <Music />, pdf: <FileText />, video: <Video /> };
  return (
    <div className="card-warm p-6" data-testid={`resource-${r.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--sage)", color: "var(--brand)" }}>{iconMap[r.kind]}</span>
        <span className="overline">{r.kind}</span>
        {r.is_gated && <span className="overline text-[10px]" style={{ color: "var(--terracotta)" }}>· {t({ en: "Client-only", fr: "Client·es seulement" })}</span>}
      </div>
      <h3 className="font-serif text-xl mb-2" style={{ color: "var(--ink)" }}>{r.title}</h3>
      <p className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>{r.description}</p>
      {r.locked ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--terracotta)" }}><Lock size={14} /> {t({ en: "Sign in to access", fr: "Connectez-vous pour y accéder" })}</div>
      ) : (
        <>
          {r.kind === "audio" && <AudioPlayer url={r.url} />}
          {r.kind === "pdf" && <a href={r.url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm py-2 px-4 inline-flex" data-testid={`btn-download-${r.id}`}><Download size={14} /> {t({ en: "Download PDF", fr: "Télécharger le PDF" })}</a>}
          {r.kind === "video" && <div className="aspect-video mt-2 rounded-lg overflow-hidden"><iframe src={r.url} className="w-full h-full" title={r.title} allowFullScreen /></div>}
        </>
      )}
    </div>
  );
}

function AudioPlayer({ url }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const upd = () => setProgress((el.currentTime / (el.duration || 1)) * 100);
    el.addEventListener("timeupdate", upd);
    el.addEventListener("ended", () => setPlaying(false));
    return () => el.removeEventListener("timeupdate", upd);
  }, []);
  const toggle = () => { const el = ref.current; if (!el) return; if (playing) el.pause(); else el.play(); setPlaying(!playing); };
  return (
    <div className="mt-2">
      <audio ref={ref} src={url} preload="metadata" />
      <div className="flex items-center gap-3">
        <button onClick={toggle} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--terracotta)", color: "white" }} data-testid="audio-play">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "var(--border-subtle)" }}>
          <div className="h-full transition-all" style={{ width: `${progress}%`, background: "var(--brand)" }} />
        </div>
      </div>
    </div>
  );
}
